import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Canvas, FabricImage, IText, Line, Point, Path, Group, Rect } from 'fabric';
import type { TEvent } from 'fabric';
import { 
  ZoomIn, 
  ZoomOut, 
  Move, 
  Type, 
  Minus, 
  Save, 
  Download, 
  RotateCcw, 
  MousePointer,
  Ruler,
  Eye,
  Maximize,
  Compass,
  Settings,
  FileCode,
  FileJson,
  Undo2,
  Redo2,
  Grid3X3,
  Square,
  Magnet,
  ShoppingCart,
  Coins,
  AlertCircle
} from 'lucide-react';
import { 
  NorthPoint, 
  ExportConfig, 
  DisclaimerSettings, 
  AreaSummary,
  DEFAULT_EXPORT_CONFIG,
  DEFAULT_DISCLAIMER_SETTINGS,
  Calibration as CalibrationTypes,
  BrandingProfile,
  DEFAULT_BRANDING_PROFILE,
} from '../types';
import { 
  NORTH_POINT_SYMBOLS, 
  getNorthPointSymbol, 
  DEFAULT_NORTH_POINT_SIZE 
} from '../constants/northPointSymbols';
import NorthPointPicker, { NorthPointButton } from './NorthPointPicker';
import ExportSettingsModal from './ExportSettingsModal';
import { formatAreaBoth } from '../utils/areaCalculation';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface Measurement {
  id: string;
  type: 'label' | 'line';
  x: number;
  y: number;
  text?: string;
  // For lines
  x2?: number;
  y2?: number;
  // Metadata
  fontSize?: number;
  fontFamily?: string;
}

export interface FloorPlanData {
  measurements: Measurement[];
  calibration?: {
    pixelsPerFoot: number;
    referenceLineId?: string;
  };
}

export interface FloorPlanEditorProps {
  imageUrl: string;
  initialData?: FloorPlanData;
  onSave?: (data: FloorPlanData, imageBlob: Blob | null) => void;
  width?: number;
  height?: number;
  className?: string;
  /** Enhanced look (beta) - apply enhanced styling without changing geometry */
  enhancedLook?: boolean;
  /** Credits required to download */
  downloadCreditCost?: number;
  /** Current user credits */
  userCredits?: number;
  /** Callback to consume credits before download */
  onConsumeCredits?: (amount: number) => Promise<boolean>;
  /** Callback to order professional floor plan */
  onOrderPro?: (sketchUrl: string) => void;
  /** Whether user is logged in */
  isLoggedIn?: boolean;
  /** Agency branding profile (colours, fonts, watermark, logo) */
  brandingProfile?: BrandingProfile;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_FONT_SIZE = 16;
const DEFAULT_FONT_FAMILY = 'Arial';
const LINE_STROKE_WIDTH = 8; // Thick walls for visibility
const LINE_COLOR = '#000000';
const TEXT_BACKGROUND_COLOR = '#FFFFFF';
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;
const WALL_STROKE_WIDTH = 8; // Thick walls for visibility

// Grid and snapping settings
const GRID_SIZE = 20; // Grid cell size in pixels
const GRID_COLOR = 'rgba(100, 116, 139, 0.3)'; // Slate-500 with transparency
const SNAP_THRESHOLD = 15; // Distance in pixels to trigger endpoint snapping
const SNAP_INDICATOR_COLOR = '#22d3ee'; // Cyan for snap indicators

// Enhanced look styling (beta) - applies visual polish without changing geometry
const ENHANCED_WALL_COLOR = '#1a1a2e';
const ENHANCED_WALL_STROKE_WIDTH = 6;
const ENHANCED_ROOM_FILL = 'rgba(240, 248, 255, 0.3)'; // Light blue room fill
const ENHANCED_SHADOW_COLOR = 'rgba(0, 0, 0, 0.15)';
const ENHANCED_SHADOW_BLUR = 8;
const ENHANCED_SHADOW_OFFSET = 2;

// Set default origin to left/top for predictable positioning (v7 changed default to center)
const DEFAULT_ORIGIN = { originX: 'left' as const, originY: 'top' as const };

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Snap a point to the grid
 */
function snapPointToGrid(point: { x: number; y: number }, gridSize: number): { x: number; y: number } {
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
}

/**
 * Constrain a line to 90° angles (orthogonal mode)
 */
function constrainToOrtho(start: { x: number; y: number }, end: { x: number; y: number }): { x: number; y: number } {
  const dx = Math.abs(end.x - start.x);
  const dy = Math.abs(end.y - start.y);
  
  // Snap to horizontal or vertical based on which axis has greater movement
  if (dx > dy) {
    return { x: end.x, y: start.y }; // Horizontal line
  } else {
    return { x: start.x, y: end.y }; // Vertical line
  }
}

/**
 * Calculate distance between two points
 */
function distance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Format pixel length for display (during drawing)
 */
function formatLength(pixelLength: number, calibration: { pixelsPerFoot: number } | null): string {
  if (calibration) {
    const feet = pixelLength / calibration.pixelsPerFoot;
    if (feet < 1) {
      const inches = Math.round(feet * 12);
      return `${inches}"`;
    }
    const wholeFeet = Math.floor(feet);
    const inches = Math.round((feet - wholeFeet) * 12);
    if (inches === 12) {
      return `${wholeFeet + 1}'0"`;
    }
    return `${wholeFeet}'${inches}"`;
  }
  return `${Math.round(pixelLength)}px`;
}

/**
 * Escape special characters for XML/SVG
 */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FloorPlanEditor: React.FC<FloorPlanEditorProps> = ({
  imageUrl,
  initialData,
  onSave,
  width = 800,
  height = 600,
  className = '',
  enhancedLook = false,
  downloadCreditCost = 2,
  userCredits = 0,
  onConsumeCredits,
  onOrderPro,
  isLoggedIn = false,
  brandingProfile = DEFAULT_BRANDING_PROFILE,
}) => {
  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State
  const [tool, setTool] = useState<'select' | 'pan' | 'text' | 'line' | 'calibrate' | 'rect'>('select');
  const [zoom, setZoom] = useState(1);
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [lineStartPoint, setLineStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [tempLine, setTempLine] = useState<Line | null>(null);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibration, setCalibration] = useState<{ pixelsPerFoot: number } | null>(
    initialData?.calibration || null
  );
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState<{ x: number; y: number } | null>(null);
  
  // CAD-style editing state
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [snapToEndpoints, setSnapToEndpoints] = useState(true);
  const [orthoMode, setOrthoMode] = useState(false); // Toggle with Shift key
  const [isShiftDown, setIsShiftDown] = useState(false);
  
  // Undo/redo state
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const isUndoingRef = useRef(false);
  
  // Rectangle drawing state
  const [isDrawingRect, setIsDrawingRect] = useState(false);
  const [rectStartPoint, setRectStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [tempRect, setTempRect] = useState<Rect | null>(null);
  
  // Length display during drawing
  const [drawingLength, setDrawingLength] = useState<string | null>(null);
  const [drawingLabelPos, setDrawingLabelPos] = useState<{ x: number; y: number } | null>(null);
  
  // Background opacity for "tracing paper" effect
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.5);
  const backgroundImageRef = useRef<FabricImage | null>(null);
  
  // North Point state
  const [northPoint, setNorthPoint] = useState<NorthPoint | null>(null);
  const [showNorthPointPicker, setShowNorthPointPicker] = useState(false);
  const northPointObjectRef = useRef<Group | null>(null);
  
  // Export settings state
  const [exportConfig, setExportConfig] = useState<ExportConfig>(DEFAULT_EXPORT_CONFIG);
  const [disclaimerSettings, setDisclaimerSettings] = useState<DisclaimerSettings>(DEFAULT_DISCLAIMER_SETTINGS);
  const [showExportSettings, setShowExportSettings] = useState(false);
  
  // Area calculation state
  const [areaSummary, setAreaSummary] = useState<AreaSummary | null>(null);
  
  // Download confirmation state
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [pendingDownloadType, setPendingDownloadType] = useState<'png' | 'svg' | 'json' | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // ============================================================================
  // CANVAS INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (!canvasRef.current) return;

    // Initialize Fabric.js canvas
    const canvas = new Canvas(canvasRef.current, {
      width,
      height,
      backgroundColor: '#f0f0f0',
      selection: true,
      preserveObjectStacking: true,
    });

    fabricCanvasRef.current = canvas;

    // Load background image
    FabricImage.fromURL(imageUrl, { crossOrigin: 'anonymous' }).then((img) => {
      if (!img || !fabricCanvasRef.current) return;
      
      // Scale image to fit canvas while maintaining aspect ratio
      const scaleX = width / (img.width || 1);
      const scaleY = height / (img.height || 1);
      const scale = Math.min(scaleX, scaleY, 1);

      img.scale(scale);
      img.set({
        ...DEFAULT_ORIGIN,
        left: (width - (img.width || 0) * scale) / 2,
        top: (height - (img.height || 0) * scale) / 2,
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        opacity: backgroundOpacity, // Apply initial opacity for "tracing paper" effect
      });

      // Store reference for opacity updates
      backgroundImageRef.current = img;

      // Set as background
      fabricCanvasRef.current.backgroundImage = img;
      fabricCanvasRef.current.renderAll();
      
      // Load initial measurements if provided (after background is set)
      if (initialData?.measurements && initialData.measurements.length > 0) {
        console.log('Loading measurements after background image loaded');
        loadMeasurements(initialData.measurements, fabricCanvasRef.current);
        
        // Zoom to fit after a short delay to ensure everything is rendered
        setTimeout(() => {
          if (fabricCanvasRef.current) {
            console.log('Calling zoomToFit after measurements loaded');
            // Get bounding box and center view
            const objects = fabricCanvasRef.current.getObjects();
            if (objects.length > 0) {
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              objects.forEach(obj => {
                const bound = obj.getBoundingRect();
                minX = Math.min(minX, bound.left);
                minY = Math.min(minY, bound.top);
                maxX = Math.max(maxX, bound.left + bound.width);
                maxY = Math.max(maxY, bound.top + bound.height);
              });
              
              const objectsWidth = maxX - minX;
              const objectsHeight = maxY - minY;
              const objectsCenterX = minX + objectsWidth / 2;
              const objectsCenterY = minY + objectsHeight / 2;
              
              const padding = 50;
              const availableWidth = width - padding * 2;
              const availableHeight = height - padding * 2;
              
              const scaleX = availableWidth / objectsWidth;
              const scaleY = availableHeight / objectsHeight;
              const fitZoom = Math.min(scaleX, scaleY, MAX_ZOOM);
              
              const canvasCenterX = width / 2;
              const canvasCenterY = height / 2;
              const offsetX = canvasCenterX - objectsCenterX * fitZoom;
              const offsetY = canvasCenterY - objectsCenterY * fitZoom;
              
              fabricCanvasRef.current.setViewportTransform([fitZoom, 0, 0, fitZoom, offsetX, offsetY]);
              setZoom(fitZoom);
              fabricCanvasRef.current.renderAll();
              
              console.log(`Auto zoom-to-fit applied: zoom=${fitZoom.toFixed(2)}`);
            }
          }
        }, 100);
      }
    }).catch((err) => {
      console.error('Error loading background image:', err);
    });

    // Cleanup
    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
      backgroundImageRef.current = null;
    };
  }, [imageUrl, width, height]);

  // Update background opacity when slider changes
  useEffect(() => {
    if (backgroundImageRef.current && fabricCanvasRef.current) {
      backgroundImageRef.current.set({ opacity: backgroundOpacity });
      fabricCanvasRef.current.renderAll();
    }
  }, [backgroundOpacity]);

  // ============================================================================
  // GRID OVERLAY
  // ============================================================================

  const gridRef = useRef<Line[]>([]);
  
  const renderGrid = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    
    // Remove existing grid lines
    gridRef.current.forEach(line => canvas.remove(line));
    gridRef.current = [];
    
    if (!showGrid) {
      canvas.renderAll();
      return;
    }
    
    const canvasWidth = canvas.width || width;
    const canvasHeight = canvas.height || height;
    
    // Draw vertical lines
    for (let x = 0; x <= canvasWidth; x += GRID_SIZE) {
      const line = new Line([x, 0, x, canvasHeight], {
        stroke: GRID_COLOR,
        strokeWidth: 1,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        data: { type: 'grid' },
      });
      gridRef.current.push(line);
      canvas.add(line);
      canvas.sendObjectToBack(line);
    }
    
    // Draw horizontal lines
    for (let y = 0; y <= canvasHeight; y += GRID_SIZE) {
      const line = new Line([0, y, canvasWidth, y], {
        stroke: GRID_COLOR,
        strokeWidth: 1,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        data: { type: 'grid' },
      });
      gridRef.current.push(line);
      canvas.add(line);
      canvas.sendObjectToBack(line);
    }
    
    canvas.renderAll();
  }, [showGrid, width, height]);

  useEffect(() => {
    // Render grid after canvas is initialized
    const timer = setTimeout(renderGrid, 200);
    return () => clearTimeout(timer);
  }, [renderGrid]);

  // ============================================================================
  // UNDO/REDO SYSTEM
  // ============================================================================

  const saveState = useCallback(() => {
    if (!fabricCanvasRef.current || isUndoingRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    
    // Serialize only user-created objects (not grid)
    const objects = canvas.getObjects().filter(obj => {
      const data = (obj as any).data;
      return data && data.type !== 'grid';
    });
    
    const state = JSON.stringify(objects.map(obj => obj.toJSON(['data'])));
    
    setUndoStack(prev => {
      const newStack = [...prev, state];
      // Limit stack size
      if (newStack.length > 50) newStack.shift();
      return newStack;
    });
    
    // Clear redo stack on new action
    setRedoStack([]);
  }, []);

  const undo = useCallback(() => {
    if (!fabricCanvasRef.current || undoStack.length === 0) return;
    
    isUndoingRef.current = true;
    const canvas = fabricCanvasRef.current;
    
    // Save current state to redo stack
    const currentObjects = canvas.getObjects().filter(obj => {
      const data = (obj as any).data;
      return data && data.type !== 'grid';
    });
    const currentState = JSON.stringify(currentObjects.map(obj => obj.toJSON(['data'])));
    setRedoStack(prev => [...prev, currentState]);
    
    // Pop and apply previous state
    const newStack = [...undoStack];
    const previousState = newStack.pop();
    setUndoStack(newStack);
    
    if (previousState) {
      // Remove user objects
      const toRemove = canvas.getObjects().filter(obj => {
        const data = (obj as any).data;
        return data && data.type !== 'grid';
      });
      toRemove.forEach(obj => canvas.remove(obj));
      
      // Restore objects from state
      const objects = JSON.parse(previousState);
      objects.forEach((objData: any) => {
        if (objData.type === 'i-text') {
          const text = new IText(objData.text || '', {
            ...DEFAULT_ORIGIN,
            left: objData.left,
            top: objData.top,
            fontSize: objData.fontSize,
            fontFamily: objData.fontFamily,
            fill: objData.fill,
            backgroundColor: objData.backgroundColor,
            data: objData.data,
          });
          canvas.add(text);
        } else if (objData.type === 'line') {
          const line = new Line([objData.x1, objData.y1, objData.x2, objData.y2], {
            ...DEFAULT_ORIGIN,
            stroke: objData.stroke,
            strokeWidth: objData.strokeWidth,
            data: objData.data,
          });
          canvas.add(line);
        }
      });
      
      canvas.renderAll();
    }
    
    isUndoingRef.current = false;
  }, [undoStack]);

  const redo = useCallback(() => {
    if (!fabricCanvasRef.current || redoStack.length === 0) return;
    
    isUndoingRef.current = true;
    const canvas = fabricCanvasRef.current;
    
    // Save current state to undo stack
    const currentObjects = canvas.getObjects().filter(obj => {
      const data = (obj as any).data;
      return data && data.type !== 'grid';
    });
    const currentState = JSON.stringify(currentObjects.map(obj => obj.toJSON(['data'])));
    setUndoStack(prev => [...prev, currentState]);
    
    // Pop and apply redo state
    const newStack = [...redoStack];
    const nextState = newStack.pop();
    setRedoStack(newStack);
    
    if (nextState) {
      // Remove user objects
      const toRemove = canvas.getObjects().filter(obj => {
        const data = (obj as any).data;
        return data && data.type !== 'grid';
      });
      toRemove.forEach(obj => canvas.remove(obj));
      
      // Restore objects from state
      const objects = JSON.parse(nextState);
      objects.forEach((objData: any) => {
        if (objData.type === 'i-text') {
          const text = new IText(objData.text || '', {
            ...DEFAULT_ORIGIN,
            left: objData.left,
            top: objData.top,
            fontSize: objData.fontSize,
            fontFamily: objData.fontFamily,
            fill: objData.fill,
            backgroundColor: objData.backgroundColor,
            data: objData.data,
          });
          canvas.add(text);
        } else if (objData.type === 'line') {
          const line = new Line([objData.x1, objData.y1, objData.x2, objData.y2], {
            ...DEFAULT_ORIGIN,
            stroke: objData.stroke,
            strokeWidth: objData.strokeWidth,
            data: objData.data,
          });
          canvas.add(line);
        }
      });
      
      canvas.renderAll();
    }
    
    isUndoingRef.current = false;
  }, [redoStack]);

  // Track object modifications for undo
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    
    const handleObjectModified = () => saveState();
    const handleObjectAdded = (e: any) => {
      const data = e.target?.data;
      if (data && data.type !== 'grid') {
        saveState();
      }
    };
    
    canvas.on('object:modified', handleObjectModified);
    canvas.on('object:added', handleObjectAdded);
    
    return () => {
      canvas.off('object:modified', handleObjectModified);
      canvas.off('object:added', handleObjectAdded);
    };
  }, [saveState]);

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      
      // Track shift key for ortho mode
      if (e.key === 'Shift') {
        setIsShiftDown(true);
      }
      
      // Undo: Ctrl+Z / Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      
      // Redo: Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      
      // Delete selected object
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!fabricCanvasRef.current) return;
        const canvas = fabricCanvasRef.current;
        const activeObject = canvas.getActiveObject();
        if (activeObject && !(activeObject instanceof IText && (activeObject as IText).isEditing)) {
          canvas.remove(activeObject);
          canvas.discardActiveObject();
          canvas.renderAll();
          saveState();
        }
        return;
      }
      
      // Tool shortcuts
      switch (e.key.toLowerCase()) {
        case 'v':
          setTool('select');
          break;
        case 'h':
          setTool('pan');
          break;
        case 't':
          setTool('text');
          break;
        case 'l':
          setTool('line');
          break;
        case 'r':
          setTool('rect');
          break;
        case 'g':
          setShowGrid(prev => !prev);
          break;
        case 'escape':
          setTool('select');
          // Cancel current drawing
          if (tempLine && fabricCanvasRef.current) {
            fabricCanvasRef.current.remove(tempLine);
            setTempLine(null);
            setIsDrawingLine(false);
            setLineStartPoint(null);
          }
          if (tempRect && fabricCanvasRef.current) {
            fabricCanvasRef.current.remove(tempRect);
            setTempRect(null);
            setIsDrawingRect(false);
            setRectStartPoint(null);
          }
          break;
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftDown(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [undo, redo, saveState, tempLine, tempRect]);

  // ============================================================================
  // SNAP TO ENDPOINTS
  // ============================================================================

  const findNearestEndpoint = useCallback((point: { x: number; y: number }): { x: number; y: number } | null => {
    if (!fabricCanvasRef.current || !snapToEndpoints) return null;
    
    const canvas = fabricCanvasRef.current;
    let nearestPoint: { x: number; y: number } | null = null;
    let minDistance = SNAP_THRESHOLD;
    
    canvas.getObjects().forEach(obj => {
      const data = (obj as any).data;
      if (data?.type !== 'line') return;
      
      const line = obj as Line;
      const endpoints = [
        { x: line.x1 || 0, y: line.y1 || 0 },
        { x: line.x2 || 0, y: line.y2 || 0 },
      ];
      
      endpoints.forEach(ep => {
        const d = distance(point, ep);
        if (d < minDistance) {
          minDistance = d;
          nearestPoint = ep;
        }
      });
    });
    
    return nearestPoint;
  }, [snapToEndpoints]);

  // ============================================================================
  // LOAD MEASUREMENTS FROM DATA
  // ============================================================================

  const loadMeasurements = useCallback((measurements: Measurement[], canvas: Canvas) => {
    console.log('='.repeat(60));
    console.log('LOADING MEASUREMENTS INTO CANVAS');
    console.log(`Canvas size: ${canvas.width}x${canvas.height}`);
    console.log(`Total measurements: ${measurements.length}`);
    console.log(`Enhanced look: ${enhancedLook}`);
    console.log('Measurements data:', JSON.stringify(measurements, null, 2));
    
    let lineCount = 0;
    let labelCount = 0;
    
    // Styling based on enhanced look toggle + branding
    const wallColor = brandingProfile.useColoredWalls
      ? brandingProfile.primaryColor
      : enhancedLook ? ENHANCED_WALL_COLOR : '#000000';
    const wallWidth = enhancedLook ? ENHANCED_WALL_STROKE_WIDTH : WALL_STROKE_WIDTH;
    const textColor = enhancedLook ? ENHANCED_WALL_COLOR : LINE_COLOR;
    const fontFamily = brandingProfile.useBrandFont ? brandingProfile.fontFamily : DEFAULT_FONT_FAMILY;
    
    measurements.forEach((m) => {
      if (m.type === 'label' && m.text) {
        console.log(`Adding label "${m.text}" at (${m.x}, ${m.y})`);
        const text = new IText(m.text, {
          ...DEFAULT_ORIGIN,
          left: m.x,
          top: m.y,
          fontSize: m.fontSize || DEFAULT_FONT_SIZE,
          fontFamily: m.fontFamily || fontFamily,
          fill: textColor,
          backgroundColor: TEXT_BACKGROUND_COLOR,
          padding: 4,
          editable: true,
          selectable: true,
          data: { id: m.id, type: 'label' },
          // Enhanced look shadow
          ...(enhancedLook ? {
            shadow: {
              color: ENHANCED_SHADOW_COLOR,
              blur: ENHANCED_SHADOW_BLUR / 2,
              offsetX: ENHANCED_SHADOW_OFFSET / 2,
              offsetY: ENHANCED_SHADOW_OFFSET / 2,
            }
          } : {}),
        });
        canvas.add(text);
        labelCount++;
      } else if (m.type === 'line' && m.x2 !== undefined && m.y2 !== undefined) {
        console.log(`Adding line from (${m.x}, ${m.y}) to (${m.x2}, ${m.y2})`);
        const line = new Line([m.x, m.y, m.x2, m.y2], {
          ...DEFAULT_ORIGIN,
          stroke: wallColor,
          strokeWidth: wallWidth,
          strokeUniform: true, // Consistent stroke regardless of scale
          selectable: true,
          hasControls: true,
          data: { id: m.id, type: 'line' },
          // Enhanced look shadow for walls
          ...(enhancedLook ? {
            shadow: {
              color: ENHANCED_SHADOW_COLOR,
              blur: ENHANCED_SHADOW_BLUR,
              offsetX: ENHANCED_SHADOW_OFFSET,
              offsetY: ENHANCED_SHADOW_OFFSET,
            }
          } : {}),
        });
        canvas.add(line);
        lineCount++;

        // Add measurement label at the center of the line if text exists
        if (m.text) {
          const centerX = (m.x + m.x2) / 2;
          const centerY = (m.y + m.y2) / 2;
          const labelText = new IText(m.text, {
            ...DEFAULT_ORIGIN,
            left: centerX,
            top: centerY - 20,
            fontSize: m.fontSize || DEFAULT_FONT_SIZE,
            fontFamily: m.fontFamily || fontFamily,
            fill: textColor,
            backgroundColor: TEXT_BACKGROUND_COLOR,
            padding: 4,
            editable: true,
            selectable: true,
            data: { id: `${m.id}-label`, type: 'label', parentLineId: m.id },
          });
          canvas.add(labelText);
          labelCount++;
        }
      }
    });
    
    console.log(`LOADED: ${lineCount} lines, ${labelCount} labels`);
    console.log('='.repeat(60));
    
    canvas.renderAll();
  }, [enhancedLook]);

  // Update styles when enhancedLook changes
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    const wallColor = enhancedLook ? ENHANCED_WALL_COLOR : '#000000';
    const wallWidth = enhancedLook ? ENHANCED_WALL_STROKE_WIDTH : WALL_STROKE_WIDTH;
    const textColor = enhancedLook ? ENHANCED_WALL_COLOR : LINE_COLOR;
    const shadowConfig = enhancedLook ? {
      color: ENHANCED_SHADOW_COLOR,
      blur: ENHANCED_SHADOW_BLUR,
      offsetX: ENHANCED_SHADOW_OFFSET,
      offsetY: ENHANCED_SHADOW_OFFSET,
    } : null;
    
    canvas.getObjects().forEach(obj => {
      const data = (obj as any).data;
      if (!data) return;
      
      if (data.type === 'line') {
        (obj as Line).set({
          stroke: wallColor,
          strokeWidth: wallWidth,
          shadow: shadowConfig,
        });
      } else if (data.type === 'label') {
        (obj as IText).set({
          fill: textColor,
          shadow: shadowConfig ? {
            ...shadowConfig,
            blur: shadowConfig.blur / 2,
            offsetX: shadowConfig.offsetX / 2,
            offsetY: shadowConfig.offsetY / 2,
          } : null,
        });
      }
    });
    
    canvas.renderAll();
  }, [enhancedLook]);

  // Apply branding colours & font to canvas objects
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;

    canvas.getObjects().forEach(obj => {
      const data = (obj as any).data;
      if (!data) return;

      if (data.type === 'line' && brandingProfile.useColoredWalls) {
        (obj as Line).set({ stroke: brandingProfile.primaryColor });
      } else if (data.type === 'line' && !brandingProfile.useColoredWalls) {
        (obj as Line).set({ stroke: enhancedLook ? ENHANCED_WALL_COLOR : '#000000' });
      }

      if (data.type === 'label' && brandingProfile.useBrandFont) {
        (obj as IText).set({ fontFamily: brandingProfile.fontFamily });
      }
    });

    canvas.renderAll();
  }, [brandingProfile.useColoredWalls, brandingProfile.primaryColor, brandingProfile.useBrandFont, brandingProfile.fontFamily, enhancedLook]);

  // ============================================================================
  // ZOOM HANDLING
  // ============================================================================

  const handleZoom = useCallback((delta: number) => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    let newZoom = zoom + delta;
    newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

    const center = canvas.getCenterPoint();
    canvas.zoomToPoint(new Point(center.x, center.y), newZoom);
    setZoom(newZoom);
    canvas.renderAll();
  }, [zoom]);

  const handleZoomIn = useCallback(() => handleZoom(ZOOM_STEP), [handleZoom]);
  const handleZoomOut = useCallback(() => handleZoom(-ZOOM_STEP), [handleZoom]);

  const handleResetZoom = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    setZoom(1);
    canvas.renderAll();
  }, []);

  // Zoom to fit all objects in the canvas view
  const zoomToFit = useCallback(() => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    
    if (objects.length === 0) {
      console.log('No objects to zoom to fit');
      return;
    }
    
    // Get bounding box of all objects
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    objects.forEach(obj => {
      const bound = obj.getBoundingRect();
      minX = Math.min(minX, bound.left);
      minY = Math.min(minY, bound.top);
      maxX = Math.max(maxX, bound.left + bound.width);
      maxY = Math.max(maxY, bound.top + bound.height);
    });
    
    const objectsWidth = maxX - minX;
    const objectsHeight = maxY - minY;
    const objectsCenterX = minX + objectsWidth / 2;
    const objectsCenterY = minY + objectsHeight / 2;
    
    console.log(`Zoom to fit: objects bounds (${minX.toFixed(0)}, ${minY.toFixed(0)}) to (${maxX.toFixed(0)}, ${maxY.toFixed(0)})`);
    console.log(`Objects size: ${objectsWidth.toFixed(0)}x${objectsHeight.toFixed(0)}, center: (${objectsCenterX.toFixed(0)}, ${objectsCenterY.toFixed(0)})`);
    
    // Calculate zoom to fit with padding
    const padding = 50;
    const availableWidth = (canvas.width || width) - padding * 2;
    const availableHeight = (canvas.height || height) - padding * 2;
    
    const scaleX = availableWidth / objectsWidth;
    const scaleY = availableHeight / objectsHeight;
    const newZoom = Math.min(scaleX, scaleY, MAX_ZOOM);
    
    console.log(`Calculated zoom: ${newZoom.toFixed(2)}`);
    
    // Reset viewport and apply new zoom centered on objects
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    
    const canvasCenterX = (canvas.width || width) / 2;
    const canvasCenterY = (canvas.height || height) / 2;
    
    // Calculate offset to center the objects
    const offsetX = canvasCenterX - objectsCenterX * newZoom;
    const offsetY = canvasCenterY - objectsCenterY * newZoom;
    
    canvas.setViewportTransform([newZoom, 0, 0, newZoom, offsetX, offsetY]);
    setZoom(newZoom);
    canvas.renderAll();
    
    console.log(`Viewport set: zoom=${newZoom.toFixed(2)}, offset=(${offsetX.toFixed(0)}, ${offsetY.toFixed(0)})`);
  }, [width, height]);

  // Mouse wheel zoom
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleWheel = (opt: TEvent<WheelEvent>) => {
      const e = opt.e;
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      let newZoom = zoom + delta;
      newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

      const pointer = canvas.getViewportPoint(e);
      canvas.zoomToPoint(pointer, newZoom);
      setZoom(newZoom);
      canvas.renderAll();
    };

    canvas.on('mouse:wheel', handleWheel);

    return () => {
      canvas.off('mouse:wheel', handleWheel);
    };
  }, [zoom]);

  // ============================================================================
  // PAN HANDLING
  // ============================================================================

  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleMouseDown = (opt: TEvent<MouseEvent>) => {
      if (tool !== 'pan') return;
      
      setIsPanning(true);
      const pointer = canvas.getViewportPoint(opt.e);
      setLastPanPoint({ x: pointer.x, y: pointer.y });
      canvas.selection = false;
      canvas.setCursor('grabbing');
    };

    const handleMouseMove = (opt: TEvent<MouseEvent>) => {
      if (!isPanning || !lastPanPoint || tool !== 'pan') return;

      const pointer = canvas.getViewportPoint(opt.e);
      const vpt = canvas.viewportTransform;
      if (!vpt) return;

      vpt[4] += pointer.x - lastPanPoint.x;
      vpt[5] += pointer.y - lastPanPoint.y;
      
      canvas.setViewportTransform(vpt);
      setLastPanPoint({ x: pointer.x, y: pointer.y });
      canvas.renderAll();
    };

    const handleMouseUp = () => {
      setIsPanning(false);
      setLastPanPoint(null);
      if (tool === 'pan') {
        canvas.setCursor('grab');
      }
      canvas.selection = tool === 'select';
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [tool, isPanning, lastPanPoint]);

  // ============================================================================
  // LINE DRAWING (with snapping and ortho mode)
  // ============================================================================

  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleMouseDown = (opt: TEvent<MouseEvent>) => {
      if (tool !== 'line' && tool !== 'calibrate') return;

      let pointer = canvas.getScenePoint(opt.e);
      
      // Apply snapping
      if (snapToEndpoints) {
        const snapped = findNearestEndpoint(pointer);
        if (snapped) pointer = new Point(snapped.x, snapped.y);
      }
      if (snapToGrid && !snapToEndpoints) {
        const snapped = snapPointToGrid(pointer, GRID_SIZE);
        pointer = new Point(snapped.x, snapped.y);
      }
      
      setIsDrawingLine(true);
      setLineStartPoint({ x: pointer.x, y: pointer.y });

      // Create temporary line
      const activeLine_COLOR = brandingProfile.useColoredWalls ? brandingProfile.primaryColor : LINE_COLOR;
      const line = new Line([pointer.x, pointer.y, pointer.x, pointer.y], {
        ...DEFAULT_ORIGIN,
        stroke: tool === 'calibrate' ? '#FF0000' : activeLine_COLOR,
        strokeWidth: LINE_STROKE_WIDTH,
        selectable: false,
        evented: false,
      });
      canvas.add(line);
      setTempLine(line);
    };

    const handleMouseMove = (opt: TEvent<MouseEvent>) => {
      if (!isDrawingLine || !lineStartPoint || !tempLine) return;

      let pointer = canvas.getScenePoint(opt.e);
      let endPoint = { x: pointer.x, y: pointer.y };
      
      // Apply ortho mode if shift is held
      if (isShiftDown || orthoMode) {
        endPoint = constrainToOrtho(lineStartPoint, endPoint);
      }
      
      // Apply snapping
      if (snapToEndpoints) {
        const snapped = findNearestEndpoint(endPoint);
        if (snapped) endPoint = snapped;
      } else if (snapToGrid) {
        endPoint = snapPointToGrid(endPoint, GRID_SIZE);
      }
      
      tempLine.set({
        x2: endPoint.x,
        y2: endPoint.y,
      });
      
      // Update length display
      const pixelLength = distance(lineStartPoint, endPoint);
      setDrawingLength(formatLength(pixelLength, calibration));
      setDrawingLabelPos({
        x: (lineStartPoint.x + endPoint.x) / 2,
        y: (lineStartPoint.y + endPoint.y) / 2 - 30,
      });
      
      canvas.renderAll();
    };

    const handleMouseUp = (opt: TEvent<MouseEvent>) => {
      if (!isDrawingLine || !lineStartPoint || !tempLine) return;

      let pointer = canvas.getScenePoint(opt.e);
      let endPoint = { x: pointer.x, y: pointer.y };
      
      // Apply ortho mode if shift is held
      if (isShiftDown || orthoMode) {
        endPoint = constrainToOrtho(lineStartPoint, endPoint);
      }
      
      // Apply snapping
      if (snapToEndpoints) {
        const snapped = findNearestEndpoint(endPoint);
        if (snapped) endPoint = snapped;
      } else if (snapToGrid) {
        endPoint = snapPointToGrid(endPoint, GRID_SIZE);
      }
      
      // Remove temp line
      canvas.remove(tempLine);

      // Calculate line length
      const pixelLength = distance(lineStartPoint, endPoint);

      // Only create line if it has some length
      if (pixelLength > 10) {
        if (tool === 'calibrate') {
          // Prompt for real-world distance
          const realDistance = prompt('Enter the real-world distance for this line (e.g., "10" for 10 feet):');
          if (realDistance) {
            const feet = parseFloat(realDistance);
            if (!isNaN(feet) && feet > 0) {
              const pixelsPerFoot = pixelLength / feet;
              setCalibration({ pixelsPerFoot });
              setIsCalibrating(false);
              setTool('select');
              alert(`Calibration set: ${pixelsPerFoot.toFixed(2)} pixels per foot`);
            }
          }
        } else {
          // Create permanent line
          const id = generateId();
          const permanentLineColor = brandingProfile.useColoredWalls ? brandingProfile.primaryColor : LINE_COLOR;
          const line = new Line([lineStartPoint.x, lineStartPoint.y, endPoint.x, endPoint.y], {
            ...DEFAULT_ORIGIN,
            stroke: permanentLineColor,
            strokeWidth: LINE_STROKE_WIDTH,
            selectable: true,
            hasControls: true,
            data: { id, type: 'line' },
          });
          canvas.add(line);

          // Calculate measurement text if calibrated
          const measurementText = formatLength(pixelLength, calibration);

          // Add measurement label
          const centerX = (lineStartPoint.x + endPoint.x) / 2;
          const centerY = (lineStartPoint.y + endPoint.y) / 2;
          const label = new IText(measurementText, {
            ...DEFAULT_ORIGIN,
            left: centerX,
            top: centerY - 20,
            fontSize: DEFAULT_FONT_SIZE,
            fontFamily: brandingProfile.useBrandFont ? brandingProfile.fontFamily : DEFAULT_FONT_FAMILY,
            fill: LINE_COLOR,
            backgroundColor: TEXT_BACKGROUND_COLOR,
            padding: 4,
            editable: true,
            selectable: true,
            data: { id: `${id}-label`, type: 'label', parentLineId: id },
          });
          canvas.add(label);
        }
        canvas.renderAll();
      }

      setIsDrawingLine(false);
      setLineStartPoint(null);
      setTempLine(null);
      setDrawingLength(null);
      setDrawingLabelPos(null);
    };

    if (tool === 'line' || tool === 'calibrate') {
      canvas.on('mouse:down', handleMouseDown);
      canvas.on('mouse:move', handleMouseMove);
      canvas.on('mouse:up', handleMouseUp);
    }

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [tool, isDrawingLine, lineStartPoint, tempLine, calibration, isShiftDown, orthoMode, snapToGrid, snapToEndpoints, findNearestEndpoint]);

  // ============================================================================
  // RECTANGLE ROOM TOOL
  // ============================================================================

  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleMouseDown = (opt: TEvent<MouseEvent>) => {
      if (tool !== 'rect') return;

      let pointer = canvas.getScenePoint(opt.e);
      
      // Apply snapping
      if (snapToGrid) {
        pointer = new Point(...Object.values(snapPointToGrid(pointer, GRID_SIZE)));
      }
      
      setIsDrawingRect(true);
      setRectStartPoint({ x: pointer.x, y: pointer.y });

      // Create temporary rectangle (shown as outline)
      const rect = new Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: 'transparent',
        stroke: SNAP_INDICATOR_COLOR,
        strokeWidth: 2,
        strokeDashArray: [5, 5],
        selectable: false,
        evented: false,
      });
      canvas.add(rect);
      setTempRect(rect);
    };

    const handleMouseMove = (opt: TEvent<MouseEvent>) => {
      if (!isDrawingRect || !rectStartPoint || !tempRect) return;

      let pointer = canvas.getScenePoint(opt.e);
      
      // Apply ortho mode (square) if shift is held
      if (isShiftDown) {
        const dx = pointer.x - rectStartPoint.x;
        const dy = pointer.y - rectStartPoint.y;
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        pointer = new Point(
          rectStartPoint.x + (dx > 0 ? size : -size),
          rectStartPoint.y + (dy > 0 ? size : -size)
        );
      }
      
      // Apply snapping
      if (snapToGrid) {
        pointer = new Point(...Object.values(snapPointToGrid(pointer, GRID_SIZE)));
      }
      
      const left = Math.min(rectStartPoint.x, pointer.x);
      const top = Math.min(rectStartPoint.y, pointer.y);
      const width = Math.abs(pointer.x - rectStartPoint.x);
      const height = Math.abs(pointer.y - rectStartPoint.y);
      
      tempRect.set({ left, top, width, height });
      
      // Update length display (shows dimensions)
      const widthStr = formatLength(width, calibration);
      const heightStr = formatLength(height, calibration);
      setDrawingLength(`${widthStr} × ${heightStr}`);
      setDrawingLabelPos({ x: left + width / 2, y: top - 30 });
      
      canvas.renderAll();
    };

    const handleMouseUp = (opt: TEvent<MouseEvent>) => {
      if (!isDrawingRect || !rectStartPoint || !tempRect) return;

      let pointer = canvas.getScenePoint(opt.e);
      
      // Apply ortho mode (square) if shift is held
      if (isShiftDown) {
        const dx = pointer.x - rectStartPoint.x;
        const dy = pointer.y - rectStartPoint.y;
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        pointer = new Point(
          rectStartPoint.x + (dx > 0 ? size : -size),
          rectStartPoint.y + (dy > 0 ? size : -size)
        );
      }
      
      // Apply snapping
      if (snapToGrid) {
        pointer = new Point(...Object.values(snapPointToGrid(pointer, GRID_SIZE)));
      }
      
      // Remove temp rectangle
      canvas.remove(tempRect);
      
      const left = Math.min(rectStartPoint.x, pointer.x);
      const top = Math.min(rectStartPoint.y, pointer.y);
      const right = Math.max(rectStartPoint.x, pointer.x);
      const bottom = Math.max(rectStartPoint.y, pointer.y);
      const width = right - left;
      const height = bottom - top;

      // Only create rectangle if it has some size
      if (width > 20 && height > 20) {
        // Create 4 walls as individual lines
        const baseId = generateId();
        const walls = [
          { x1: left, y1: top, x2: right, y2: top },       // Top wall
          { x1: right, y1: top, x2: right, y2: bottom },   // Right wall
          { x1: right, y1: bottom, x2: left, y2: bottom }, // Bottom wall
          { x1: left, y1: bottom, x2: left, y2: top },     // Left wall
        ];
        
        const rectWallColor = brandingProfile.useColoredWalls ? brandingProfile.primaryColor : LINE_COLOR;
        walls.forEach((wall, idx) => {
          const id = `${baseId}-wall-${idx}`;
          const line = new Line([wall.x1, wall.y1, wall.x2, wall.y2], {
            ...DEFAULT_ORIGIN,
            stroke: rectWallColor,
            strokeWidth: LINE_STROKE_WIDTH,
            selectable: true,
            hasControls: true,
            data: { id, type: 'line' },
          });
          canvas.add(line);
        });
        
        // Add room label at center
        const labelId = `${baseId}-label`;
        const label = new IText('Room', {
          ...DEFAULT_ORIGIN,
          left: left + width / 2 - 20,
          top: top + height / 2 - 10,
          fontSize: DEFAULT_FONT_SIZE,
          fontFamily: brandingProfile.useBrandFont ? brandingProfile.fontFamily : DEFAULT_FONT_FAMILY,
          fill: LINE_COLOR,
          backgroundColor: TEXT_BACKGROUND_COLOR,
          padding: 4,
          editable: true,
          selectable: true,
          data: { id: labelId, type: 'label' },
        });
        canvas.add(label);
        canvas.setActiveObject(label);
        label.enterEditing();
        label.selectAll();
        
        canvas.renderAll();
      }

      setIsDrawingRect(false);
      setRectStartPoint(null);
      setTempRect(null);
      setDrawingLength(null);
      setDrawingLabelPos(null);
    };

    if (tool === 'rect') {
      canvas.on('mouse:down', handleMouseDown);
      canvas.on('mouse:move', handleMouseMove);
      canvas.on('mouse:up', handleMouseUp);
    }

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [tool, isDrawingRect, rectStartPoint, tempRect, calibration, isShiftDown, snapToGrid]);

  // ============================================================================
  // TEXT TOOL
  // ============================================================================

  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleMouseDown = (opt: TEvent<MouseEvent>) => {
      if (tool !== 'text') return;
      
      // Check if we clicked on an existing object
      const target = canvas.findTarget(opt.e);
      if (target) return;

      const pointer = canvas.getScenePoint(opt.e);
      const id = generateId();
      
      const text = new IText('Label', {
        ...DEFAULT_ORIGIN,
        left: pointer.x,
        top: pointer.y,
        fontSize: DEFAULT_FONT_SIZE,
        fontFamily: brandingProfile.useBrandFont ? brandingProfile.fontFamily : DEFAULT_FONT_FAMILY,
        fill: LINE_COLOR,
        backgroundColor: TEXT_BACKGROUND_COLOR,
        padding: 4,
        editable: true,
        selectable: true,
        data: { id, type: 'label' },
      });

      canvas.add(text);
      canvas.setActiveObject(text);
      text.enterEditing();
      text.selectAll();
      canvas.renderAll();
    };

    if (tool === 'text') {
      canvas.on('mouse:down', handleMouseDown);
    }

    return () => {
      canvas.off('mouse:down', handleMouseDown);
    };
  }, [tool]);

  // ============================================================================
  // TOOL CURSOR MANAGEMENT
  // ============================================================================

  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    switch (tool) {
      case 'select':
        canvas.defaultCursor = 'default';
        canvas.hoverCursor = 'move';
        canvas.selection = true;
        canvas.forEachObject((obj) => {
          obj.selectable = true;
          obj.evented = true;
        });
        break;
      case 'pan':
        canvas.defaultCursor = 'grab';
        canvas.hoverCursor = 'grab';
        canvas.selection = false;
        canvas.forEachObject((obj) => {
          obj.selectable = false;
          obj.evented = false;
        });
        break;
      case 'text':
        canvas.defaultCursor = 'text';
        canvas.hoverCursor = 'text';
        canvas.selection = false;
        break;
      case 'line':
      case 'calibrate':
      case 'rect':
        canvas.defaultCursor = 'crosshair';
        canvas.hoverCursor = 'crosshair';
        canvas.selection = false;
        break;
    }
    canvas.renderAll();
  }, [tool]);

  // ============================================================================
  // EXPORT FUNCTIONS
  // ============================================================================

  const extractMeasurements = useCallback((): Measurement[] => {
    if (!fabricCanvasRef.current) return [];

    const measurements: Measurement[] = [];
    const canvas = fabricCanvasRef.current;

    canvas.forEachObject((obj) => {
      const data = (obj as any).data;
      if (!data) return;

      if (data.type === 'label' && obj instanceof IText) {
        measurements.push({
          id: data.id,
          type: 'label',
          x: obj.left || 0,
          y: obj.top || 0,
          text: obj.text || '',
          fontSize: obj.fontSize,
          fontFamily: obj.fontFamily as string,
        });
      } else if (data.type === 'line' && obj instanceof Line) {
        measurements.push({
          id: data.id,
          type: 'line',
          x: obj.x1 || 0,
          y: obj.y1 || 0,
          x2: obj.x2 || 0,
          y2: obj.y2 || 0,
        });
      }
    });

    return measurements;
  }, []);

  // ============================================================================
  // NORTH POINT RENDERING
  // ============================================================================

  const renderNorthPoint = useCallback((np: NorthPoint) => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    const canvasWidth = canvas.width || width;
    const canvasHeight = canvas.height || height;
    
    // Remove existing north point if any
    if (northPointObjectRef.current) {
      canvas.remove(northPointObjectRef.current);
      northPointObjectRef.current = null;
    }
    
    // Get the symbol
    const symbol = getNorthPointSymbol(np.style);
    if (!symbol) return;
    
    // Calculate position (normalized 0-1 to canvas pixels)
    const size = DEFAULT_NORTH_POINT_SIZE * np.scale;
    const x = np.x * canvasWidth;
    const y = np.y * canvasHeight;
    
    // Create paths from symbol
    const paths: Path[] = [];
    symbol.paths.forEach((pathData, idx) => {
      const path = new Path(pathData.d, {
        fill: pathData.fill === 'currentColor' ? '#000000' : (pathData.fill || 'none'),
        stroke: pathData.stroke === 'currentColor' ? '#000000' : (pathData.stroke || 'none'),
        strokeWidth: pathData.strokeWidth || 0,
        ...DEFAULT_ORIGIN,
      });
      paths.push(path);
    });
    
    // Group the paths
    const group = new Group(paths, {
      ...DEFAULT_ORIGIN,
      left: x - size / 2,
      top: y - size / 2,
      scaleX: size / 100,
      scaleY: size / 100,
      angle: np.rotation,
      centeredRotation: true,
      selectable: true,
      hasControls: true,
      data: { id: np.id, type: 'northPoint' },
    });
    
    canvas.add(group);
    northPointObjectRef.current = group;
    canvas.renderAll();
  }, [width, height]);

  // Update north point when it changes
  useEffect(() => {
    if (northPoint) {
      renderNorthPoint(northPoint);
    } else if (northPointObjectRef.current && fabricCanvasRef.current) {
      fabricCanvasRef.current.remove(northPointObjectRef.current);
      northPointObjectRef.current = null;
      fabricCanvasRef.current.renderAll();
    }
  }, [northPoint, renderNorthPoint]);

  // ============================================================================
  // EXTENDED EXPORT WITH FOOTER
  // ============================================================================

  const exportWithFooter = useCallback(async (): Promise<Blob | null> => {
    if (!fabricCanvasRef.current) return null;
    
    const canvas = fabricCanvasRef.current;
    const { padding, includeFooter, footerHeight, format, quality, scale } = exportConfig;
    
    // Reset viewport for export
    const currentVpt = canvas.viewportTransform;
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    
    try {
      // Get base canvas as data URL
      const baseDataUrl = canvas.toDataURL({
        format: format === 'jpeg' ? 'jpeg' : 'png',
        quality: quality,
        multiplier: scale,
      });
      
      if (!includeFooter) {
        // No footer, just return the base image
        const response = await fetch(baseDataUrl);
        return await response.blob();
      }
      
      // Create extended canvas with footer
      const baseImage = new Image();
      await new Promise<void>((resolve, reject) => {
        baseImage.onload = () => resolve();
        baseImage.onerror = reject;
        baseImage.src = baseDataUrl;
      });
      
      const scaledPadding = padding * scale;
      const scaledFooterHeight = footerHeight * scale;
      
      const exportWidth = baseImage.width + scaledPadding * 2;
      const exportHeight = baseImage.height + scaledPadding * 2 + scaledFooterHeight;
      
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = exportWidth;
      exportCanvas.height = exportHeight;
      const ctx = exportCanvas.getContext('2d');
      
      if (!ctx) return null;
      
      // ── Background (use brand footer colour if footer enabled, else white) ──
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, exportWidth, exportHeight);

      // Brand-coloured header bar at top of plan area (subtle stripe)
      if (brandingProfile.primaryColor && brandingProfile.primaryColor !== '#ffffff') {
        ctx.fillStyle = brandingProfile.primaryColor;
        ctx.fillRect(0, 0, exportWidth, 6 * scale);
      }

      // Draw floor plan with padding
      ctx.drawImage(baseImage, scaledPadding, scaledPadding);

      // ── Footer ──
      const footerY = scaledPadding + baseImage.height + scaledPadding;

      // Footer background (brand colour)
      const footerBg = brandingProfile.footerBgColor ?? '#FFFFFF';
      const footerFg = brandingProfile.footerTextColor ?? '#374151';
      ctx.fillStyle = footerBg;
      ctx.fillRect(0, footerY - scaledPadding / 2, exportWidth, scaledFooterHeight + scaledPadding);

      // Footer top border in primary colour
      ctx.strokeStyle = brandingProfile.primaryColor ?? '#E5E7EB';
      ctx.lineWidth = 2 * scale;
      ctx.beginPath();
      ctx.moveTo(scaledPadding, footerY - scaledPadding / 2);
      ctx.lineTo(exportWidth - scaledPadding, footerY - scaledPadding / 2);
      ctx.stroke();

      // Calculate total area string
      const totalAreaString = areaSummary?.totalM2 
        ? formatAreaBoth(areaSummary.totalM2)
        : '— sq ft / — sqm';

      // Disclaimer text with area placeholder replaced
      const disclaimerText = disclaimerSettings.disclaimerText.replace(
        '{totalArea}',
        totalAreaString
      );

      const brandFont = brandingProfile.useBrandFont ? brandingProfile.fontFamily : 'Arial';
      const fontSize = 10 * scale;
      let textY = footerY;

      // ── Agency header row (logo + name) if branding is configured ──
      const hasAgency = brandingProfile.agencyName || (brandingProfile.showLogoInFooter && brandingProfile.logoDataUrl);
      if (hasAgency) {
        const logoPromise: Promise<HTMLImageElement | null> = brandingProfile.showLogoInFooter && brandingProfile.logoDataUrl
          ? new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve(img);
              img.onerror = () => resolve(null);
              img.src = brandingProfile.logoDataUrl!;
            })
          : Promise.resolve(null);

        const logoImg = await logoPromise;
        const logoHeight = 28 * scale;

        if (logoImg) {
          const logoWidth = logoImg.width * (logoHeight / logoImg.height);
          ctx.drawImage(logoImg, exportWidth - scaledPadding - logoWidth, textY - logoHeight + 4 * scale, logoWidth, logoHeight);
        }

        if (brandingProfile.agencyName) {
          ctx.font = `bold ${13 * scale}px ${brandFont}`;
          ctx.fillStyle = footerFg;
          ctx.fillText(brandingProfile.agencyName, scaledPadding, textY);
          textY += 16 * scale;
        }

        if (brandingProfile.tagline) {
          ctx.font = `${9 * scale}px ${brandFont}`;
          ctx.fillStyle = brandingProfile.accentColor ?? footerFg;
          ctx.fillText(brandingProfile.tagline, scaledPadding, textY);
          textY += 14 * scale;
        }

        // Thin separator line
        ctx.strokeStyle = (brandingProfile.primaryColor ?? '#E5E7EB') + '55';
        ctx.lineWidth = 1 * scale;
        ctx.beginPath();
        ctx.moveTo(scaledPadding, textY + 4 * scale);
        ctx.lineTo(exportWidth - scaledPadding, textY + 4 * scale);
        ctx.stroke();
        textY += 12 * scale;
      }

      // Property name
      if (disclaimerSettings.propertyName) {
        ctx.font = `bold ${14 * scale}px ${brandFont}`;
        ctx.fillStyle = footerFg;
        ctx.fillText(disclaimerSettings.propertyName, scaledPadding, textY);
        textY += 20 * scale;
      }

      // Disclaimer text (multi-line)
      ctx.font = `${fontSize}px ${brandFont}`;
      ctx.fillStyle = footerFg;
      const lines = disclaimerText.split('\n');
      lines.forEach((line) => {
        ctx.fillText(line, scaledPadding, textY);
        textY += fontSize * 1.4;
      });

      // Date and legacy branding row
      if (disclaimerSettings.showDate || disclaimerSettings.showBranding) {
        textY += 10 * scale;
        ctx.fillStyle = footerFg + 'aa';
        ctx.font = `${9 * scale}px ${brandFont}`;

        if (disclaimerSettings.showDate) {
          const dateStr = `date: ${new Date().toLocaleDateString('en-GB')}`;
          ctx.fillText(dateStr, scaledPadding, textY);
        }

        if (disclaimerSettings.showBranding && disclaimerSettings.brandingText) {
          const brandWidth = ctx.measureText(disclaimerSettings.brandingText).width;
          ctx.font = `bold ${9 * scale}px ${brandFont}`;
          ctx.fillStyle = footerFg;
          ctx.fillText(
            disclaimerSettings.brandingText, 
            exportWidth - scaledPadding - brandWidth, 
            textY
          );
        }
      }
      
      // Convert to blob
      return new Promise<Blob | null>((resolve) => {
        exportCanvas.toBlob(
          (blob) => resolve(blob),
          format === 'jpeg' ? 'image/jpeg' : 'image/png',
          quality
        );
      });
    } finally {
      // Restore viewport
      if (currentVpt) {
        canvas.setViewportTransform(currentVpt);
      }
    }
  }, [exportConfig, disclaimerSettings, areaSummary, width, height, brandingProfile]);

  const handleSave = useCallback(async () => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const measurements = extractMeasurements();

    const data: FloorPlanData = {
      measurements,
      calibration: calibration || undefined,
    };

    // Export as image
    let imageBlob: Blob | null = null;
    try {
      // Reset viewport transform for export
      const currentVpt = canvas.viewportTransform;
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

      const dataUrl = canvas.toDataURL({
        format: 'jpeg',
        quality: 0.95,
        multiplier: 2,
      });

      // Restore viewport
      if (currentVpt) {
        canvas.setViewportTransform(currentVpt);
      }

      // Convert data URL to Blob
      const response = await fetch(dataUrl);
      imageBlob = await response.blob();
    } catch (err) {
      console.error('Error exporting image:', err);
    }

    if (onSave) {
      onSave(data, imageBlob);
    }

    return { data, imageBlob };
  }, [extractMeasurements, calibration, onSave]);

  // ============================================================================
  // VECTOR EXPORT FUNCTIONS (Geometry Authority)
  // Must be defined before performDownload which uses them
  // ============================================================================

  /**
   * Export floor plan as SVG using vector geometry
   * All exports come from vector data to ensure geometry accuracy
   */
  const exportToSVG = useCallback((): string => {
    const measurements = extractMeasurements();
    const svgWidth = width;
    const svgHeight = height;
    
    // Build SVG elements from measurements
    const lines = measurements
      .filter(m => m.type === 'line' && m.x2 !== undefined && m.y2 !== undefined)
      .map(m => `    <line x1="${m.x}" y1="${m.y}" x2="${m.x2}" y2="${m.y2}" stroke="#000000" stroke-width="8" stroke-linecap="round" />`);
    
    const labels = measurements
      .filter(m => m.type === 'label' && m.text)
      .map(m => `    <text x="${m.x}" y="${m.y}" font-family="Arial" font-size="${m.fontSize || 16}" fill="#000000">${escapeXml(m.text || '')}</text>`);
    
    // Add north point if set
    let northPointSvg = '';
    if (northPoint) {
      const symbol = getNorthPointSymbol(northPoint.style);
      if (symbol) {
        const size = DEFAULT_NORTH_POINT_SIZE * northPoint.scale;
        const x = northPoint.x * svgWidth - size / 2;
        const y = northPoint.y * svgHeight - size / 2;
        northPointSvg = `    <g transform="translate(${x}, ${y}) rotate(${northPoint.rotation}, ${size/2}, ${size/2}) scale(${size/100})">
${symbol.paths.map(p => `      <path d="${p.d}" fill="${p.fill === 'currentColor' ? '#000' : (p.fill || 'none')}" stroke="${p.stroke === 'currentColor' ? '#000' : (p.stroke || 'none')}" stroke-width="${p.strokeWidth || 0}" />`).join('\n')}
    </g>`;
      }
    }
    
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
  <!-- Walls -->
  <g id="walls">
${lines.join('\n')}
  </g>
  
  <!-- Labels -->
  <g id="labels">
${labels.join('\n')}
  </g>
  
  <!-- North Point -->
${northPointSvg}
</svg>`;
    
    return svg;
  }, [extractMeasurements, width, height, northPoint]);

  /**
   * Export floor plan as JSON using vector geometry
   * Provides structured data for external tools and integrations
   */
  const exportToJSON = useCallback((): string => {
    const measurements = extractMeasurements();
    
    const walls = measurements
      .filter(m => m.type === 'line')
      .map(m => ({
        id: m.id,
        x1: m.x,
        y1: m.y,
        x2: m.x2,
        y2: m.y2,
      }));
    
    const labels = measurements
      .filter(m => m.type === 'label')
      .map(m => ({
        id: m.id,
        x: m.x,
        y: m.y,
        text: m.text,
        fontSize: m.fontSize,
      }));
    
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      dimensions: {
        width,
        height,
      },
      calibration: calibration || null,
      areaSummary: areaSummary ? {
        totalM2: areaSummary.totalM2,
        totalFt2: areaSummary.totalFt2,
        confidence: areaSummary.confidence,
        hasScale: areaSummary.hasScale,
      } : null,
      northPoint: northPoint || null,
      disclaimerSettings,
      geometry: {
        walls,
        labels,
      },
    };
    
    return JSON.stringify(exportData, null, 2);
  }, [extractMeasurements, width, height, calibration, areaSummary, northPoint, disclaimerSettings]);

  // Actually perform the download after credit confirmation
  const performDownload = useCallback(async (type: 'png' | 'svg' | 'json') => {
    setIsDownloading(true);
    
    try {
      if (type === 'png') {
        // Use extended export with footer if enabled
        let imageBlob: Blob | null = null;
        
        if (exportConfig.includeFooter) {
          imageBlob = await exportWithFooter();
        } else {
          const result = await handleSave();
          imageBlob = result?.imageBlob || null;
        }
        
        if (!imageBlob) return;

        const extension = exportConfig.format === 'jpeg' ? 'jpg' : 'png';
        const url = URL.createObjectURL(imageBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `floor-plan-${Date.now()}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (type === 'svg') {
        const svg = exportToSVG();
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `floor-plan-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (type === 'json') {
        const json = exportToJSON();
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `floor-plan-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } finally {
      setIsDownloading(false);
      setShowDownloadConfirm(false);
      setPendingDownloadType(null);
    }
  }, [exportConfig, exportWithFooter, handleSave, exportToSVG, exportToJSON]);

  // Request download - shows confirmation if credits needed
  const requestDownload = useCallback((type: 'png' | 'svg' | 'json') => {
    if (!isLoggedIn) {
      alert('Please log in to download floor plans.');
      return;
    }
    
    if (onConsumeCredits && downloadCreditCost > 0) {
      setPendingDownloadType(type);
      setShowDownloadConfirm(true);
    } else {
      // No credit system, just download
      performDownload(type);
    }
  }, [isLoggedIn, onConsumeCredits, downloadCreditCost, performDownload]);

  // Confirm download and consume credits
  const confirmDownload = useCallback(async () => {
    if (!pendingDownloadType || !onConsumeCredits) return;
    
    setIsDownloading(true);
    
    try {
      const success = await onConsumeCredits(downloadCreditCost);
      if (success) {
        await performDownload(pendingDownloadType);
      } else {
        alert('Insufficient credits. Please purchase more credits to download.');
        setShowDownloadConfirm(false);
        setPendingDownloadType(null);
        setIsDownloading(false);
      }
    } catch (err) {
      console.error('Error consuming credits:', err);
      alert('Error processing download. Please try again.');
      setShowDownloadConfirm(false);
      setPendingDownloadType(null);
      setIsDownloading(false);
    }
  }, [pendingDownloadType, onConsumeCredits, downloadCreditCost, performDownload]);

  // Legacy handler for backwards compatibility
  const handleDownload = useCallback(async () => {
    requestDownload('png');
  }, [requestDownload]);

  /**
   * Download SVG file (with credit check)
   */
  const handleDownloadSVG = useCallback(() => {
    requestDownload('svg');
  }, [requestDownload]);

  /**
   * Download JSON file (free - no credit needed for data export)
   */
  const handleDownloadJSON = useCallback(() => {
    const json = exportToJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `floor-plan-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [exportToJSON]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`flex flex-col bg-slate-900 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-slate-800 border-b border-slate-700 px-4 py-2">
        <div className="flex items-center gap-1">
          {/* Undo/Redo */}
          <button
            onClick={undo}
            disabled={undoStack.length === 0}
            className={`p-2 rounded-lg transition-colors ${
              undoStack.length > 0
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-600 cursor-not-allowed'
            }`}
            title="Undo (Ctrl+Z)"
          >
            <Undo2 size={18} />
          </button>
          <button
            onClick={redo}
            disabled={redoStack.length === 0}
            className={`p-2 rounded-lg transition-colors ${
              redoStack.length > 0
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-600 cursor-not-allowed'
            }`}
            title="Redo (Ctrl+Shift+Z)"
          >
            <Redo2 size={18} />
          </button>

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* Selection Tool */}
          <button
            onClick={() => setTool('select')}
            className={`p-2 rounded-lg transition-colors ${
              tool === 'select'
                ? 'bg-teal-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title="Select / Move (V)"
          >
            <MousePointer size={18} />
          </button>

          {/* Pan Tool */}
          <button
            onClick={() => setTool('pan')}
            className={`p-2 rounded-lg transition-colors ${
              tool === 'pan'
                ? 'bg-teal-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title="Pan (H)"
          >
            <Move size={18} />
          </button>

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* Text Tool */}
          <button
            onClick={() => setTool('text')}
            className={`p-2 rounded-lg transition-colors ${
              tool === 'text'
                ? 'bg-teal-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title="Add Text Label (T)"
          >
            <Type size={18} />
          </button>

          {/* Line Tool */}
          <button
            onClick={() => setTool('line')}
            className={`p-2 rounded-lg transition-colors ${
              tool === 'line'
                ? 'bg-teal-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title="Draw Wall (L) - Hold Shift for orthogonal"
          >
            <Minus size={18} />
          </button>

          {/* Rectangle Room Tool */}
          <button
            onClick={() => setTool('rect')}
            className={`p-2 rounded-lg transition-colors ${
              tool === 'rect'
                ? 'bg-teal-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title="Draw Room (R) - Creates 4 walls"
          >
            <Square size={18} />
          </button>

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* Grid Toggle */}
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`p-2 rounded-lg transition-colors ${
              showGrid
                ? 'bg-slate-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title="Toggle Grid (G)"
          >
            <Grid3X3 size={18} />
          </button>

          {/* Snap Toggle */}
          <button
            onClick={() => {
              if (snapToGrid && snapToEndpoints) {
                setSnapToGrid(false);
                setSnapToEndpoints(false);
              } else if (!snapToGrid && !snapToEndpoints) {
                setSnapToGrid(true);
                setSnapToEndpoints(false);
              } else {
                setSnapToGrid(true);
                setSnapToEndpoints(true);
              }
            }}
            className={`p-2 rounded-lg transition-colors ${
              snapToGrid || snapToEndpoints
                ? 'bg-cyan-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title={`Snap: ${snapToEndpoints ? 'Endpoints' : snapToGrid ? 'Grid' : 'Off'}`}
          >
            <Magnet size={18} />
          </button>

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* Calibrate Tool */}
          <button
            onClick={() => setTool('calibrate')}
            className={`p-2 rounded-lg transition-colors ${
              tool === 'calibrate'
                ? 'bg-amber-600 text-white'
                : calibration
                ? 'text-amber-400 hover:text-amber-300 hover:bg-slate-700'
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
            title={calibration ? `Calibrated: ${calibration.pixelsPerFoot.toFixed(1)} px/ft` : 'Set Scale - Draw a line of known length'}
          >
            <Ruler size={18} />
          </button>
          {calibration && (
            <span className="text-xs text-amber-400 ml-1">
              {calibration.pixelsPerFoot.toFixed(1)} px/ft
            </span>
          )}

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* Sketch Opacity Slider */}
          <div className="flex items-center gap-2">
            <Eye size={16} className="text-slate-400" />
            <span className="text-xs text-slate-400 whitespace-nowrap hidden xl:inline">Sketch</span>
            <input
              type="range"
              min="0"
              max="100"
              value={backgroundOpacity * 100}
              onChange={(e) => setBackgroundOpacity(parseInt(e.target.value) / 100)}
              className="w-16 h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-teal-500"
              title={`Sketch Opacity: ${Math.round(backgroundOpacity * 100)}%`}
            />
            <span className="text-xs text-slate-400 w-8">
              {Math.round(backgroundOpacity * 100)}%
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Zoom Controls */}
          <button
            onClick={handleZoomOut}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            title="Zoom Out (-)"
          >
            <ZoomOut size={18} />
          </button>
          <span className="text-sm text-slate-400 min-w-[60px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            title="Zoom In (+)"
          >
            <ZoomIn size={18} />
          </button>
          <button
            onClick={handleResetZoom}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            title="Reset Zoom (0)"
          >
            <RotateCcw size={18} />
          </button>
          <button
            onClick={zoomToFit}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            title="Zoom to Fit All"
          >
            <Maximize size={18} />
          </button>

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* North Point */}
          <NorthPointButton
            hasNorthPoint={!!northPoint}
            onClick={() => setShowNorthPointPicker(true)}
          />

          <div className="w-px h-6 bg-slate-700 mx-2" />

          {/* Save / Download */}
          <button
            onClick={handleSave}
            className="p-2 rounded-lg text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-colors"
            title="Save (Ctrl+S)"
          >
            <Save size={18} />
          </button>
          <button
            onClick={() => setShowExportSettings(true)}
            className="p-2 rounded-lg text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-colors"
            title="Export Settings"
          >
            <Settings size={18} />
          </button>

          {/* Branding active indicator */}
          {(brandingProfile.agencyName || brandingProfile.logoDataUrl) && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-800 border border-slate-700"
              title={`Branding: ${brandingProfile.agencyName || 'Logo set'}`}
            >
              <div className="w-3 h-3 rounded-full" style={{ background: brandingProfile.primaryColor }} />
              {brandingProfile.logoDataUrl && (
                <img src={brandingProfile.logoDataUrl} alt="" className="h-4 w-auto object-contain opacity-80" />
              )}
              {brandingProfile.agencyName && (
                <span className="text-[10px] text-slate-400 max-w-[80px] truncate">{brandingProfile.agencyName}</span>
              )}
            </div>
          )}
          <button
            onClick={handleDownload}
            className="p-2 rounded-lg text-slate-400 hover:text-teal-400 hover:bg-slate-700 transition-colors"
            title="Download PNG/JPEG"
          >
            <Download size={18} />
          </button>
          <button
            onClick={handleDownloadSVG}
            className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-slate-700 transition-colors"
            title="Download SVG (Vector)"
          >
            <FileCode size={18} />
          </button>
          <button
            onClick={handleDownloadJSON}
            className="p-2 rounded-lg text-slate-400 hover:text-purple-400 hover:bg-slate-700 transition-colors"
            title="Download JSON (Data) - Free"
          >
            <FileJson size={18} />
          </button>

          {onOrderPro && (
            <>
              <div className="w-px h-6 bg-slate-700 mx-2" />
              <button
                onClick={() => onOrderPro(imageUrl)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-sm font-medium transition-all shadow-lg shadow-amber-900/20"
                title="Order a professionally drawn floor plan from our team"
              >
                <ShoppingCart size={16} />
                <span className="hidden lg:inline">Order Pro</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Canvas Container */}
      <div 
        ref={containerRef}
        className="flex-1 flex items-center justify-center bg-slate-950 overflow-hidden relative"
        style={{ minHeight: height }}
      >
        <canvas ref={canvasRef} />
        
        {/* Drawing length display overlay */}
        {drawingLength && drawingLabelPos && (
          <div
            className="absolute pointer-events-none bg-slate-900/90 text-cyan-400 px-3 py-1.5 rounded-lg text-sm font-mono shadow-lg border border-cyan-500/30"
            style={{
              left: drawingLabelPos.x,
              top: drawingLabelPos.y,
              transform: 'translate(-50%, -50%)',
              zIndex: 100,
            }}
          >
            {drawingLength}
          </div>
        )}
        
        {/* Ortho mode indicator */}
        {isShiftDown && (isDrawingLine || isDrawingRect) && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-cyan-600 text-white px-3 py-1 rounded text-xs font-medium">
            ORTHO (90°)
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between bg-slate-800 border-t border-slate-700 px-4 py-1.5">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span>
            Tool: <span className="text-slate-300 capitalize">{tool}</span>
          </span>
          <span>
            Snap: <span className={snapToEndpoints ? 'text-cyan-400' : snapToGrid ? 'text-cyan-400' : 'text-slate-500'}>
              {snapToEndpoints ? 'Endpoints' : snapToGrid ? 'Grid' : 'Off'}
            </span>
          </span>
          {calibration ? (
            <span>
              Scale: <span className="text-amber-400">{calibration.pixelsPerFoot.toFixed(1)} px/ft</span>
            </span>
          ) : (
            <span className="text-amber-500/70">
              Scale: <span className="italic">not set</span>
              <button 
                onClick={() => setTool('calibrate')}
                className="ml-1 underline hover:text-amber-400 transition-colors"
              >
                Set dimension
              </button>
            </span>
          )}
          {areaSummary?.hasScale && areaSummary.totalFt2 !== null ? (
            <span>
              Area: <span className="text-green-400">{Math.round(areaSummary.totalFt2)} sq ft</span>
              <span className="text-slate-500 ml-1">/ {Math.round(areaSummary.totalM2 || 0)} sqm</span>
            </span>
          ) : (
            <span className="text-slate-500 italic hidden lg:inline">
              Set scale to calculate area
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 hidden md:block">
          <span>L: Line • R: Room • T: Text • Shift: Ortho • Del: Delete • Ctrl+Z: Undo</span>
        </div>
      </div>

      {/* North Point Picker Modal */}
      <NorthPointPicker
        isOpen={showNorthPointPicker}
        onClose={() => setShowNorthPointPicker(false)}
        currentNorthPoint={northPoint}
        onNorthPointChange={setNorthPoint}
      />

      {/* Export Settings Modal */}
      <ExportSettingsModal
        isOpen={showExportSettings}
        onClose={() => setShowExportSettings(false)}
        exportConfig={exportConfig}
        onExportConfigChange={setExportConfig}
        disclaimerSettings={disclaimerSettings}
        onDisclaimerSettingsChange={setDisclaimerSettings}
        onExport={handleDownload}
        totalAreaString={areaSummary?.totalM2 ? formatAreaBoth(areaSummary.totalM2) : undefined}
      />

      {/* Download Confirmation Modal */}
      {showDownloadConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl border border-slate-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-teal-600/20 flex items-center justify-center">
                <Coins size={24} className="text-teal-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Download Floor Plan</h3>
                <p className="text-sm text-slate-400">This download costs credits</p>
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400">Download cost:</span>
                <span className="text-xl font-bold text-teal-400">{downloadCreditCost} credits</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Your balance:</span>
                <span className={`text-lg font-medium ${userCredits >= downloadCreditCost ? 'text-green-400' : 'text-red-400'}`}>
                  {userCredits} credits
                </span>
              </div>
              {userCredits < downloadCreditCost && (
                <div className="flex items-center gap-2 mt-3 text-red-400 text-sm">
                  <AlertCircle size={16} />
                  <span>Insufficient credits</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDownloadConfirm(false);
                  setPendingDownloadType(null);
                }}
                className="flex-1 py-3 rounded-xl font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 transition-colors"
                disabled={isDownloading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDownload}
                disabled={isDownloading || userCredits < downloadCreditCost}
                className="flex-1 py-3 rounded-xl font-medium text-white bg-teal-600 hover:bg-teal-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Download
                  </>
                )}
              </button>
            </div>

            {userCredits < downloadCreditCost && onOrderPro && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-sm text-slate-400 mb-3 text-center">
                  Or let our team create a professional floor plan for you
                </p>
                <button
                  onClick={() => {
                    setShowDownloadConfirm(false);
                    setPendingDownloadType(null);
                    onOrderPro(imageUrl);
                  }}
                  className="w-full py-3 rounded-xl font-medium text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 transition-all flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={18} />
                  Order Pro Floor Plan
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanEditor;
